import * as Sentry from '@sentry/react';
import download from 'downloadjs';
import {DateTime} from 'luxon';
import React from 'react';
import sanitizeFilename from 'sanitize-filename';

import {recordEvent} from 'src/components/Analytics';
import {DropdownItem} from 'src/components/Dropdown';
import {useTranslations} from 'src/components/TranslationsContext';
import {formatDateShortFull} from 'src/lib/dates';

import {ChartExport, chartToBlob, chartToPng} from './chartExport';

/** Names a downloaded chart after what it shows and when it was taken. */
export function chartDownloadFilename(subject: string): string {
  return sanitizeFilename(
    `${subject} generated ${formatDateShortFull(new Date(), DateTime.now().zone.name)}`
  );
}

/**
 * The image entries every chart's "More…" menu shares, plus the menu label,
 * which doubles as the copy-to-clipboard progress indicator.
 */
export function useChartImageMenu({
  buildChart,
  filename,
  title,
}: {
  /** Called on click, so nothing is rendered off-screen until an export is asked for. */
  buildChart: () => ChartExport | null;
  filename: () => string;
  /** Identifies the chart in analytics events. */
  title: string;
}): {label: string; items: DropdownItem[]} {
  const {t} = useTranslations();
  const [copyStatus, setCopyStatus] = React.useState<'copying' | 'copied' | null>(null);
  const clipboardSupported = typeof ClipboardItem !== 'undefined' && !!navigator.clipboard?.write;

  return {
    label:
      copyStatus === 'copying' ? t('copying') : copyStatus === 'copied' ? t('copied') : t('more'),
    items: [
      {
        props: {
          onClick: async () => {
            const chart = buildChart();
            if (!chart) return;
            try {
              download(await chartToPng(chart), `${filename()}.png`);
              recordEvent('download_image_clicked', {title});
            } catch (e) {
              Sentry.captureException(e);
            }
          },
        },
        text: t('downloadAsImage'),
      },
      {
        props: {
          hidden: !clipboardSupported,
          onClick: async () => {
            const chart = buildChart();
            if (!chart) return;
            recordEvent('copy_image_clicked', {title});
            setCopyStatus('copying');
            try {
              const blob = await chartToBlob(chart);
              if (!blob) throw new Error('Chart could not be rendered to an image');
              await navigator.clipboard.write([new ClipboardItem({'image/png': blob})]);
              setCopyStatus('copied');
              setTimeout(() => setCopyStatus(null), 2000);
            } catch (e) {
              setCopyStatus(null);
              Sentry.captureException(e);
              window.alert(t('copyFailed'));
            }
          },
        },
        text: t('copyAsImage'),
      },
    ],
  };
}
